namespace $ {

	export type $shm_hitalama_team_project_permission = 'visible' | 'prerelease' | 'release'

	export function $shm_hitalama_team_project_allowed( ids: readonly string[], project: string ) {
		return ids.includes( '*' ) || ids.includes( project )
	}

	export function $shm_hitalama_team_project_toggle(
		current: readonly string[],
		projects: readonly string[],
		project: string,
		next: boolean,
	) {
		const explicit = current.includes( '*' ) ? projects : current.filter( id => projects.includes( id ) )
		return next
			? [ ...new Set([ ...explicit, project ]) ]
			: explicit.filter( id => id !== project )
	}

	export function $shm_hitalama_team_rank_ensure(
		land: $giper_baza_land,
		pass: $giper_baza_auth_pass,
		rank: typeof $giper_baza_rank.Value,
	) {
		if( land.lord_rank( pass.lord() ) !== rank ) land.give( pass, rank )
		return rank
	}

	export class $shm_hitalama_team_secret extends $giper_baza_entity.with({
		Projects_api_token: $giper_baza_atom_text,
	}) {}

	export class $shm_hitalama_team_member extends $giper_baza_entity.with({
		User_id: $giper_baza_atom_text,
		Name: $giper_baza_atom_text,
		Projects_visible: $giper_baza_list_str,
		Projects_prerelease: $giper_baza_list_str,
		Projects_release: $giper_baza_list_str,
	}) {

		project_ids( kind: $shm_hitalama_team_project_permission, next?: readonly string[] ) {
			const list = kind === 'visible'
				? this.Projects_visible( next === undefined ? undefined : null )
				: kind === 'prerelease'
					? this.Projects_prerelease( next === undefined ? undefined : null )
					: this.Projects_release( next === undefined ? undefined : null )
			if( !list ) return [] as string[]
			return list.items( next ? [ ...next ] : undefined ) as string[]
		}

		project_allowed( kind: $shm_hitalama_team_project_permission, project: string ) {
			return $shm_hitalama_team_project_allowed( this.project_ids( kind ), project )
		}

	}

	export class $shm_hitalama_team extends $giper_baza_entity.with({
		Name: $giper_baza_atom_text,
		Creator_id: $giper_baza_atom_text,
		Projects_api_url: $giper_baza_atom_text,
		Admins: $giper_baza_list_str,
		Members: $giper_baza_list_link.to( ()=> $shm_hitalama_team_member ),
		Maps: $giper_baza_atom_link.to( ()=> $shm_hitalama_team_maps ),
		Projects_map: $giper_baza_atom_link.to( ()=> $shm_hitalama_map ),
		Secret: $giper_baza_atom_link.to( ()=> $shm_hitalama_team_secret ),
	}) {

		name( next?: string ) {
			return this.Name( next === undefined ? undefined : null )?.val( next ) ?? ''
		}

		projects_api_url( next?: string ) {
			return this.Projects_api_url( next === undefined ? undefined : null )?.val( next ) ?? ''
		}

		secret() {
			return this.Secret()?.remote() ?? null
		}

		projects_api_token( next?: string ) {
			const secret = this.secret()
			if( !secret ) return ''
			return secret.Projects_api_token( next === undefined ? undefined : null )?.val( next ) ?? ''
		}

		creator_id() {
			return this.Creator_id()?.val() ?? ''
		}

		admin_ids( next?: readonly string[] ) {
			const admins = this.Admins( next === undefined ? undefined : null )
			if( !admins ) return [] as string[]
			return admins.items( next ? [ ...next ] : undefined ) as string[]
		}

		is_admin( user_id: string ) {
			return this.creator_id() === user_id || this.admin_ids().includes( user_id )
		}

		member_records() {
			return this.Members()?.remote_list() ?? []
		}

		maps_list( auto?: null ): $shm_hitalama_team_maps | null {
			const current = this.Maps()?.remote()
			if( current || auto !== null ) return current ?? null
			if( !this.can_change() ) return null
			const list = this.land().area_make()
				.Data( this.$.$shm_hitalama_team_maps )
			for( const member of this.member_records() ) {
				const id = member.User_id()?.val()
				if( !id ) continue
				const pass = this.land().lord_pass( new this.$.$giper_baza_link( id ) )
				if( pass ) list.land().give( pass, this.$.$giper_baza_rank_rule )
			}
			this.Maps( null )!.remote( list )
			return list
		}

		map_rank_all( map: $shm_hitalama_map ) {
			if( map.land().pass_rank( this.$.$giper_baza_auth.current().pass() ) < this.$.$giper_baza_rank_rule ) return map
			for( const member of this.member_records() ) {
				const id = member.User_id()?.val()
				if( !id ) continue
				const pass = this.land().lord_pass( new this.$.$giper_baza_link( id ) )
				if( pass ) map.land().give( pass, this.$.$giper_baza_rank_rule )
			}
			return map
		}

		projects_map( next?: $shm_hitalama_map | null ) {
			return this.Projects_map( next === undefined ? undefined : null )?.remote( next ) ?? null
		}

		member_by_user( user_id: string ) {
			return this.member_records().find( member => member.User_id()?.val() === user_id ) ?? null
		}

		member_add( user_id: string, name = user_id ) {
			const existed = this.member_by_user( user_id )
			if( existed ) return existed
			const member = this.land().area_make().Data( this.$.$shm_hitalama_team_member )
			member.User_id( null )!.val( user_id )
			member.Name( null )!.val( name || user_id )
			member.Projects_visible( null )!.items([ '*' ])
			member.Projects_prerelease( null )!.items([ '*' ])
			member.Projects_release( null )!.items([ '*' ])
			this.Members( null )!.remote_add( member )
			return member
		}

		member_rank( pass: $giper_baza_auth_pass, admin: boolean ) {
			const rank = admin ? this.$.$giper_baza_rank_rule : this.$.$giper_baza_rank_read
			this.$.$shm_hitalama_team_rank_ensure( this.land(), pass, rank )
			const secret = this.secret()
			if( secret ) this.$.$shm_hitalama_team_rank_ensure( secret.land(), pass, rank )
			const maps = this.maps_list( null )
			if( maps ) this.$.$shm_hitalama_team_rank_ensure( maps.land(), pass, this.$.$giper_baza_rank_rule )
			for( const map of maps?.remote_list() ?? [] ) {
				if( map.land().pass_rank( this.$.$giper_baza_auth.current().pass() ) >= this.$.$giper_baza_rank_rule ) {
					this.$.$shm_hitalama_team_rank_ensure( map.land(), pass, this.$.$giper_baza_rank_rule )
				}
			}
			return rank
		}

		@ $mol_action
		member_register( pass: $giper_baza_auth_pass, user_id: string, name = user_id ) {
			// Publish all permissions first. If wire suspends, no incomplete member is visible.
			this.member_rank( pass, false )
			return this.member_add( user_id, name )
		}

		member_admin( user_id: string, pass: $giper_baza_auth_pass, next: boolean ) {
			if( user_id === this.creator_id() ) return true
			const current = this.admin_ids().filter( id => id !== user_id )
			this.admin_ids( next ? [ ...current, user_id ] : current )
			this.member_rank( pass, next )
			return next
		}

	}

	export class $shm_hitalama_team_catalog extends $giper_baza_entity.with({
		Teams: $giper_baza_list_link.to( ()=> $shm_hitalama_team ),
		User_names: $giper_baza_dict_to( $giper_baza_atom_text ),
		User_avatars: $giper_baza_dict_to( $giper_baza_atom_link.to( ()=> $shm_hitalama_file ) ),
	}) {}

}
